import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setInvoiceFilters, resetInvoiceFilters } from "../../../store/filters/invoiceFilterSlice";
import BaseDrawer from "../../../components/ui/BaseDrawer";
import BaseDatePicker from "../../../components/ui/BaseDatePicker";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import BaseTextField from "../../../components/ui/BaseTextField";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import useAuth from "../../../hooks/useAuth";
import { fetchCompanyUsers } from "../../../services/api/company";
import { InvoiceStatusLabel, InvoiceStatusType } from "../../../utils/enum";
import { fetchAllClients } from "../../../services/api/client";
import { customerLabel } from "../../../utils/helpers";

const CustomerInvoiceFilters = ({
  open,
  onClose,
  fetchCustomerInvoices,
  refetchStatusCounts,
  currentStatus,
  isLoading,
  setFiltersChanged,
}) => {
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.invoiceFilters.customerInvoices);
  const { auth, authRole } = useAuth();
  const recordLabel = customerLabel(authRole);
  const companyId = auth?.user?.company?.id;

  const [localFilters, setLocalFilters] = useState({
    creationStartDate: "",
    creationEndDate: "",
    dueStartDate: "",
    dueEndDate: "",
    customer: { value: "", label: "" },
    generatedBy: { value: "", label: "" },
    reviewedBy: { value: "", label: "" },
    sendBy: { value: "", label: "" },
    paidBy: { value: "", label: "" },
    status: { value: "", label: "" },
    tax: "",
    search: "",
    id: "",
  });
  const { isPending: isClientsLoading, data: clientsData } = useQuery({
    queryKey: ["clients", companyId],
    queryFn: () =>
      fetchAllClients({
        companyId: companyId?.toString(),
        isParentClient: "false",
      }),
    enabled: !!companyId,
  });
  const {
    data: companyUsers,
    isPending: isCompanyUsersApiLoading,
    error: isCompanyUsersApiError,
    refetch,
  } = useQuery({
    queryKey: ["companyUsers", filters.limit, filters.page, filters.search],
    queryFn: () => fetchCompanyUsers({ id: companyId, filter: true }),
  });
  useEffect(() => {
    if (filters) {
      setLocalFilters(filters);
    }
  }, [filters]);

  const applyFilters = () => {
    Object.entries(localFilters).forEach(([key, val]) => {
      dispatch(setInvoiceFilters({ module: "customerInvoices", filterName: key, data: val }));
    });

    // Reset pagination
    dispatch(
      setInvoiceFilters({
        module: "customerInvoices",
        filterName: "pagination",
        data: { page: 0 },
      }),
    );

    dispatch(
      setInvoiceFilters({
        module: "customerInvoices",
        filterName: "appliedFromDrawer",
        data: true,
      }),
    );

    setFiltersChanged(true);
    fetchCustomerInvoices();
    refetchStatusCounts();
    onClose();
  };

  const handleResetFilters = () => {
    dispatch(resetInvoiceFilters({ module: "customerInvoices", status: currentStatus }));
    setLocalFilters({
      creationStartDate: "",
      creationEndDate: "",
      dueStartDate: "",
      dueEndDate: "",
      customer: { value: "", label: "" },
      generatedBy: { value: "", label: "" },
      reviewedBy: { value: "", label: "" },
      sendBy: { value: "", label: "" },
      paidBy: { value: "", label: "" },
      status: { value: "", label: "" },
      tax: "",
      search: "",
      id: "",
    });
    setFiltersChanged(true);
  };
  const hasFilters = Object.values(localFilters).some((v) => v && v.toString().trim() !== "");
  return (
    <BaseDrawer
      open={open}
      onClose={onClose}
      onApply={applyFilters}
      title={
        <span
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "24px",
            color: "#686868",
            fontWeight: "400",
          }}
        >
          Search Criteria
        </span>
      }
      saveButtonText="Apply Filters"
      isLoading={isLoading}
      handleResetFilters={handleResetFilters}
      enabled={hasFilters}
    >
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4 items-center mb-4">
          <div>
            <label className="block text-sm text-[#686868]">Creation Start Date</label>
            <BaseDatePicker
              className="border-none"
              type="date"
              id="creationStartDate"
              name="creationStartDate"
              maxDate={
                localFilters.creationEndDate ? dayjs(localFilters.creationEndDate) : undefined
              }
              value={localFilters.creationStartDate ? dayjs(localFilters.creationStartDate) : null}
              onChange={(date) =>
                setLocalFilters({
                  ...localFilters,
                  creationStartDate: dayjs(date).format("YYYY-MM-DD"),
                })
              }
              slotProps={{
                textField: { sx: { ...datePickerStyles } },
              }}
            />
          </div>
          <div>
            <label className="block text-sm text-[#686868]">Creation End Date</label>
            <BaseDatePicker
              className="border-none"
              type="date"
              id="creationEndDate"
              name="creationEndDate"
              minDate={
                localFilters.creationStartDate ? dayjs(localFilters.creationStartDate) : undefined
              }
              value={localFilters.creationEndDate ? dayjs(localFilters.creationEndDate) : null}
              onChange={(date) =>
                setLocalFilters({
                  ...localFilters,
                  creationEndDate: dayjs(date).format("YYYY-MM-DD"),
                })
              }
              slotProps={{
                textField: { sx: { ...datePickerStyles } },
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-[#686868]">Due Start Date</label>
            <BaseDatePicker
              className="border-none"
              type="date"
              id="dueStartDate"
              name="dueStartDate"
              maxDate={localFilters.dueEndDate ? dayjs(localFilters.dueEndDate) : undefined}
              value={localFilters.dueStartDate ? dayjs(localFilters.dueStartDate) : null}
              onChange={(date) =>
                setLocalFilters({
                  ...localFilters,
                  dueStartDate: dayjs(date).format("YYYY-MM-DD"),
                })
              }
              slotProps={{
                textField: { sx: { ...datePickerStyles } },
              }}
            />
          </div>

          <div>
            <label className="block text-sm text-[#686868]">Due End Date</label>
            <BaseDatePicker
              className="border-none"
              type="date"
              id="dueEndDate"
              name="dueEndDate"
              minDate={localFilters.dueStartDate ? dayjs(localFilters.dueStartDate) : undefined}
              value={localFilters.dueEndDate ? dayjs(localFilters.dueEndDate) : null}
              onChange={(date) =>
                setLocalFilters({
                  ...localFilters,
                  dueEndDate: dayjs(date).format("YYYY-MM-DD"),
                })
              }
              slotProps={{
                textField: { sx: { ...datePickerStyles } },
              }}
            />
          </div>
        </div>
        <BaseAutoSelect
          required={true}
          labelShrink={false}
          label={recordLabel}
          placeholder={`Select ${recordLabel}`}
          options={clientsData?.data?.data.map((u) => ({
            label: u.clientName,
            value: u.id,
          }))}
          value={localFilters.customer.value || ""}
          onChange={(e) =>
            setLocalFilters({
              ...localFilters,
              customer: { value: e.target.value, label: e.target.label },
            })
          }
          isClearable
        />

        <BaseAutoSelect
          required={true}
          labelShrink={false}
          label="Generated By"
          placeholder="Select Generated By"
          options={companyUsers?.data?.users.map((u) => ({
            label: `${u.firstName} ${u.lastName}`,
            value: u.id,
          }))}
          value={localFilters.generatedBy.value || ""}
          onChange={(e) =>
            setLocalFilters({
              ...localFilters,
              generatedBy: { value: e.target.value, label: e.target.label },
            })
          }
          isClearable
        />

        <BaseAutoSelect
          required={true}
          labelShrink={false}
          label="Approved By"
          placeholder="Select Approved By"
          options={companyUsers?.data?.users.map((u) => ({
            label: `${u.firstName} ${u.lastName}`,
            value: u.id,
          }))}
          value={localFilters.reviewedBy.value || ""}
          onChange={(e) =>
            setLocalFilters({
              ...localFilters,
              reviewedBy: { value: e.target.value, label: e.target.label },
            })
          }
          isClearable
        />

        <BaseAutoSelect
          required={true}
          labelShrink={false}
          label="Sent By"
          placeholder="Select Sent By"
          options={companyUsers?.data?.users.map((u) => ({
            label: `${u.firstName} ${u.lastName}`,
            value: u.id,
          }))}
          value={localFilters.sendBy.value || ""}
          onChange={(e) =>
            setLocalFilters({
              ...localFilters,
              sendBy: { value: e.target.value, label: e.target.label },
            })
          }
          isClearable
        />

        <BaseAutoSelect
          required={true}
          labelShrink={false}
          label="Paid By"
          placeholder="Select Paid By"
          options={companyUsers?.data?.users.map((u) => ({
            label: `${u.firstName} ${u.lastName}`,
            value: u.id,
          }))}
          value={localFilters.paidBy.value || ""}
          onChange={(e) =>
            setLocalFilters({
              ...localFilters,
              paidBy: { value: e.target.value, label: e.target.label },
            })
          }
          isClearable
        />

        <BaseAutoSelect
          required={true}
          labelShrink={false}
          placeholder="Select Status"
          label="Status"
          options={Object.values(InvoiceStatusType).map((s) => ({
            label: InvoiceStatusLabel[s],
            value: s,
          }))}
          value={localFilters.status.value}
          onChange={(e) =>
            setLocalFilters({
              ...localFilters,
              status: { value: e.target.value, label: e.target.label },
            })
          }
          isClearable
        />

        <BaseAutoSelect
          required={true}
          labelShrink={false}
          label="Tax"
          options={[
            {
              label: "GST",
              value: "GST",
            },
            {
              label: "VAT",
              value: "VAT",
            },
          ]}
          placeholder="Select Tax"
          value={localFilters.tax}
          onChange={(e) => setLocalFilters({ ...localFilters, tax: e.target.value })}
          isClearable
        />

        <BaseTextField
          optional={false}
          labelShrink={false}
          label="Invoice ID"
          placeholder="Select Invoice ID"
          value={localFilters.id}
          onChange={(e) => setLocalFilters({ ...localFilters, id: e.target.value })}
          isClearable
        />
      </div>
    </BaseDrawer>
  );
};
const datePickerStyles = {
  "& .MuiOutlinedInput-root": {
    height: "42px",
    borderRadius: "5px",
    backgroundColor: "white",
    boxShadow: "1px 1px 4px 1px #29292921",
    display: "flex",
    alignItems: "center",
    margin: "6px 1.5px",
    border: "none !important",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none !important",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none !important",
    boxShadow: "none",
  },
  "& .MuiInputBase-input": {
    padding: "4px 10px",
    fontSize: "13px",
    height: "18px",
    lineHeight: "1.4",
  },
  "& .MuiIconButton-root": {
    padding: "4px",
    backgroundColor: "white !important",
    borderLeft: "none !important",
  },
};

export default CustomerInvoiceFilters;
